import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Paper,
} from '@mui/material';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import { Notice } from '@routes-plan/ui';
import { formatMoney } from '../inventory/shared/inventoryApi';
import { useT } from '../../i18n';

/** Producto prestable de la ruta (respuesta de `mobile/inventory/:routeId`). */
export interface LoanableProduct {
  productId: number;
  title: string;
  sku?: string | null;
  price: number;
  marginPercent?: number | string | null;
  available: number;
}

export interface SelectedLoanItem {
  productId: number;
  title: string;
  price: number;
  marginPercent: number | null;
  quantity: number;
}

export type PricingMode = 'CONSTANT' | 'PRODUCT';

export const toPercent = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Precio con la utilidad del producto (modo PRODUCT), igual que el servidor. */
export const itemTotalWithMargin = (item: SelectedLoanItem) =>
  Math.round(item.price * item.quantity * (1 + (item.marginPercent ?? 0) / 100));

interface Props {
  products: LoanableProduct[];
  items: SelectedLoanItem[];
  onChange: (items: SelectedLoanItem[]) => void;
  pricingMode: PricingMode;
  onRefresh: () => void;
  disabled?: boolean;
}

const LoanProductsPicker: React.FC<Props> = ({ products, items, onChange, pricingMode, onRefresh, disabled }) => {
  const t = useT();
  const [pending, setPending] = useState<LoanableProduct | null>(null);
  const byProduct = pricingMode === 'PRODUCT';

  const availableOf = (productId: number) =>
    Number(products.find((p) => p.productId === productId)?.available ?? 0);

  const options = useMemo(
    () => products.filter((p) => !items.some((i) => i.productId === p.productId)),
    [products, items],
  );

  const add = (product: LoanableProduct | null) => {
    if (!product || Number(product.available) <= 0) return;
    onChange([
      ...items,
      {
        productId: product.productId,
        title: product.title,
        price: Number(product.price) || 0,
        marginPercent: toPercent(product.marginPercent),
        quantity: 1,
      },
    ]);
    setPending(null);
  };

  const setQuantity = (productId: number, raw: string) => {
    const max = availableOf(productId);
    const qty = Math.max(0, Math.min(max, Math.floor(Number(raw) || 0)));
    onChange(items.map((i) => (i.productId === productId ? { ...i, quantity: qty } : i)));
  };

  const remove = (productId: number) => onChange(items.filter((i) => i.productId !== productId));

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const withMargin = items.reduce((sum, i) => sum + itemTotalWithMargin(i), 0);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
        <Typography variant="h6">{t('loans.productsTitle')}</Typography>
        <Button size="small" variant="outlined" startIcon={<RefreshOutlined />} onClick={onRefresh} disabled={disabled}>
          {t('loans.refreshAvailable')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {byProduct
          ? t('loans.pricingByProduct')
          : t('loans.pricingByLoanType')}
      </Typography>

      {products.length === 0 ? (
        <Notice severity="warning">{t('loans.noProducts')}</Notice>
      ) : (
        <Autocomplete
          size="small"
          options={options}
          value={pending}
          disabled={disabled}
          onChange={(_e, product) => add(product)}
          getOptionLabel={(p) => `${p.title}${p.sku ? ` · ${p.sku}` : ''}`}
          getOptionDisabled={(p) => Number(p.available) <= 0}
          isOptionEqualToValue={(a, b) => a.productId === b.productId}
          noOptionsText={t('loans.noProductsToAdd')}
          renderOption={(props, p) => (
            <li {...props} key={p.productId}>
              <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                <span>{p.title}{p.sku ? ` · ${p.sku}` : ''}</span>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {formatMoney(p.price)} · {t('loans.availableShort', { n: p.available })}
                </span>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label={t('loans.addProduct')} />}
        />
      )}

      {items.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('loans.product')}</TableCell>
                <TableCell align="right">{t('loans.price')}</TableCell>
                <TableCell align="center">{t('loans.quantity')}</TableCell>
                <TableCell align="right">{t('loans.subtotal')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.productId}>
                  <TableCell sx={{ minWidth: 160 }}>
                    {item.title}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('loans.availableLabel', { n: availableOf(item.productId) })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatMoney(item.price)}</TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantity}
                      disabled={disabled}
                      onChange={(e) => setQuantity(item.productId, e.target.value)}
                      inputProps={{ min: 1, max: availableOf(item.productId), step: 1, style: { width: 64, textAlign: 'center' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(byProduct ? itemTotalWithMargin(item) : item.price * item.quantity)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" aria-label={t('loans.removeItem', { name: item.title })} onClick={() => remove(item.productId)} disabled={disabled}>
                      <DeleteOutlineOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right"><strong>{t('loans.productsTotal')}</strong></TableCell>
                <TableCell align="right"><strong>{formatMoney(byProduct ? withMargin : subtotal)}</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LoanProductsPicker;
